// src/components/common/LanguageSwitcher.js
import React, { useState, useEffect, useRef } from 'react';

/* ─── All supported languages ──────────────────────────────────────── */
const LANGUAGES = [
  // International
  { code: 'en',    label: 'English',    flag: '🇬🇧', region: 'International' },
  { code: 'fr',    label: 'Français',   flag: '🇫🇷', region: 'International' },
  { code: 'de',    label: 'Deutsch',    flag: '🇩🇪', region: 'International' },
  { code: 'es',    label: 'Español',    flag: '🇪🇸', region: 'International' },
  { code: 'pt',    label: 'Português',  flag: '🇵🇹', region: 'International' },
  { code: 'it',    label: 'Italiano',   flag: '🇮🇹', region: 'International' },
  { code: 'nl',    label: 'Nederlands', flag: '🇳🇱', region: 'International' },
  { code: 'ru',    label: 'Русский',    flag: '🇷🇺', region: 'International' },
  { code: 'ar',    label: 'العربية',    flag: '🇸🇦', region: 'International' },
  { code: 'tr',    label: 'Türkçe',     flag: '🇹🇷', region: 'International' },
  { code: 'fa',    label: 'فارسی',      flag: '🇮🇷', region: 'International' },
  { code: 'sw',    label: 'Kiswahili',  flag: '🇰🇪', region: 'International' },
  // Asian
  { code: 'zh-CN', label: '中文 (简)',  flag: '🇨🇳', region: 'Asian' },
  { code: 'zh-TW', label: '中文 (繁)',  flag: '🇹🇼', region: 'Asian' },
  { code: 'ja',    label: '日本語',     flag: '🇯🇵', region: 'Asian' },
  { code: 'ko',    label: '한국어',     flag: '🇰🇷', region: 'Asian' },
  { code: 'vi',    label: 'Tiếng Việt', flag: '🇻🇳', region: 'Asian' },
  { code: 'th',    label: 'ภาษาไทย',   flag: '🇹🇭', region: 'Asian' },
  { code: 'id',    label: 'Bahasa ID',  flag: '🇮🇩', region: 'Asian' },
  { code: 'ms',    label: 'Bahasa MY',  flag: '🇲🇾', region: 'Asian' },
  // Indian
  { code: 'hi',    label: 'हिन्दी',    flag: '🇮🇳', region: 'Indian' },
  { code: 'te',    label: 'తెలుగు',    flag: '🇮🇳', region: 'Indian' },
  { code: 'ta',    label: 'தமிழ்',     flag: '🇮🇳', region: 'Indian' },
  { code: 'kn',    label: 'ಕನ್ನಡ',     flag: '🇮🇳', region: 'Indian' },
  { code: 'ml',    label: 'മലയാളം',    flag: '🇮🇳', region: 'Indian' },
  { code: 'mr',    label: 'मराठी',     flag: '🇮🇳', region: 'Indian' },
  { code: 'bn',    label: 'বাংলা',     flag: '🇮🇳', region: 'Indian' },
  { code: 'gu',    label: 'ગુજરાતી',   flag: '🇮🇳', region: 'Indian' },
  { code: 'pa',    label: 'ਪੰਜਾਬੀ',    flag: '🇮🇳', region: 'Indian' },
  { code: 'ur',    label: 'اردو',      flag: '🇮🇳', region: 'Indian' },
  { code: 'as',    label: 'অসমীয়া',   flag: '🇮🇳', region: 'Indian' },
  { code: 'or',    label: 'ଓଡ଼ିଆ',     flag: '🇮🇳', region: 'Indian' },
  { code: 'sa',    label: 'संस्कृतम्', flag: '🇮🇳', region: 'Indian' },
  { code: 'ks',    label: 'कश्मीरी',   flag: '🇮🇳', region: 'Indian' },
];

const REGIONS = ['International', 'Indian', 'Asian'];

/* ─── Cookie helpers ──────────────────────────────────────────────── */
function getGoogleCookieLang() {
  const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
  return match ? match[1] : 'en';
}

function setGoogleTranslateCookie(langCode) {
  // Google Translate reads /googtrans cookie
  const domain = window.location.hostname;
  document.cookie = `googtrans=/en/${langCode}; path=/; domain=${domain}`;
  document.cookie = `googtrans=/en/${langCode}; path=/`;
}

/* ─── Trigger Google Translate programmatically ──────────────────── */
function triggerGoogleTranslate(langCode) {
  if (langCode === 'en') {
    // Reset: remove the translation
    const frame = document.querySelector('.goog-te-combo');
    if (frame) {
      frame.value = langCode;
      frame.dispatchEvent(new Event('change'));
    }
    // Also try resetting via cookie
    setGoogleTranslateCookie('en');
    // Force reload to clear translation
    const metaRefresh = document.querySelector('html');
    if (metaRefresh) metaRefresh.lang = 'en';
    return;
  }

  setGoogleTranslateCookie(langCode);

  // Try the select element inside Google Translate iframe
  const select = document.querySelector('.goog-te-combo');
  if (select) {
    select.value = langCode;
    select.dispatchEvent(new Event('change'));
    return;
  }

  // Fallback: reload page — Google Translate will read the cookie
  window.location.reload();
}

/* ─── Main Component ─────────────────────────────────────────────── */
const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeRegion, setActiveRegion] = useState('All');
  const [currentLang, setCurrentLang] = useState('en');
  const ref = useRef(null);

  // Read current language from cookie on mount
  useEffect(() => {
    const lang = getGoogleCookieLang();
    setCurrentLang(lang);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (code) => {
    setCurrentLang(code);
    triggerGoogleTranslate(code);
    setOpen(false);
    setSearch('');
  };

  const activeLang = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  const filtered = LANGUAGES.filter(l => {
    const matchSearch = l.label.toLowerCase().includes(search.toLowerCase()) ||
                        l.code.toLowerCase().includes(search.toLowerCase());
    const matchRegion = activeRegion === 'All' || l.region === activeRegion;
    return matchSearch && matchRegion;
  });

  const grouped = REGIONS.reduce((acc, r) => {
    const items = filtered.filter(l => l.region === r);
    if (items.length) acc[r] = items;
    return acc;
  }, {});

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Change Language"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
          background: open ? '#f1f5f9' : '#fff', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: '#1a3a5c',
          transition: 'all 0.15s', whiteSpace: 'nowrap',
          boxShadow: open ? '0 0 0 3px rgba(26,58,92,0.08)' : 'none',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#1a3a5c'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = open ? '#1a3a5c' : '#e2e8f0'; }}
      >
        <span style={{ fontSize: 16 }}>{activeLang.flag}</span>
        <span>{activeLang.label}</span>
        <span style={{
          display: 'inline-block', marginLeft: 2,
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s', fontSize: 10, color: '#64748b',
        }}>▼</span>
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', borderRadius: 14, width: 320,
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)', zIndex: 99999,
          border: '1px solid #e2e8f0', overflow: 'hidden',
          animation: 'fadeSlideIn 0.15s ease',
        }}>
          {/* Header */}
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 16 }}>🌐</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                Select Language
              </span>
              <span style={{
                marginLeft: 'auto', fontSize: 11, fontWeight: 600,
                background: '#eff6ff', color: '#1d4ed8',
                padding: '2px 8px', borderRadius: 20,
              }}>
                Powered by Google
              </span>
            </div>

            {/* Search */}
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)', fontSize: 13, color: '#94a3b8',
              }}>🔍</span>
              <input
                autoFocus
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search language…"
                style={{
                  width: '100%', padding: '7px 10px 7px 30px',
                  borderRadius: 8, border: '1.5px solid #e2e8f0',
                  fontSize: 13, outline: 'none', color: '#0f172a',
                  boxSizing: 'border-box',
                }}
                onFocus={e => { e.target.style.borderColor = '#1a3a5c'; }}
                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>

            {/* Region Tabs */}
            {!search && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {['All', ...REGIONS].map(r => (
                  <button
                    key={r}
                    onClick={() => setActiveRegion(r)}
                    style={{
                      padding: '4px 12px', borderRadius: 20, border: 'none',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: activeRegion === r ? '#1a3a5c' : '#f1f5f9',
                      color: activeRegion === r ? '#fff' : '#475569',
                      transition: 'all 0.15s',
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Language List */}
          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '8px 0' }}>
            {Object.keys(grouped).length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No languages found
              </div>
            ) : (
              Object.entries(grouped).map(([region, langs]) => (
                <div key={region}>
                  {(activeRegion === 'All' || search) && (
                    <div style={{
                      padding: '6px 16px 4px', fontSize: 10, fontWeight: 700,
                      letterSpacing: 1.2, textTransform: 'uppercase', color: '#94a3b8',
                    }}>
                      {region}
                    </div>
                  )}
                  {langs.map(lang => {
                    const isActive = lang.code === currentLang;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => handleSelect(lang.code)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          width: '100%', padding: '9px 16px', border: 'none',
                          textAlign: 'left', cursor: 'pointer',
                          background: isActive ? '#eff6ff' : 'transparent',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>
                          {lang.flag}
                        </span>
                        <span style={{
                          flex: 1, fontSize: 13, fontWeight: isActive ? 700 : 500,
                          color: isActive ? '#1a3a5c' : '#334155',
                        }}>
                          {lang.label}
                        </span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>
                          {lang.code}
                        </span>
                        {isActive && (
                          <span style={{ fontSize: 14, color: '#1a3a5c', marginLeft: 4 }}>✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '10px 16px', borderTop: '1px solid #f1f5f9',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {LANGUAGES.length} languages available
            </span>
            {currentLang !== 'en' && (
              <button
                onClick={() => handleSelect('en')}
                style={{
                  fontSize: 12, fontWeight: 600, color: '#1a3a5c',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                🔄 Reset to English
              </button>
            )}
          </div>
        </div>
      )}

      {/* CSS animation */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default LanguageSwitcher;
